<script setup lang="ts">
import { computed } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import ToastContainer from '@/components/ui/ToastContainer.vue'
import AiChatDrawer from '@/components/ai/AiChatDrawer.vue'
import StepUpDialog from '@/components/security/StepUpDialog.vue'

const route = useRoute()

/**
 * The AI assistant is an operator tool — its suggested prompts are things like
 * "what's my revenue this week?". It is mounted at the app root, so without this
 * guard it also floats over the customer-facing self-care page (C1.2), where it is
 * both meaningless and a leak of internal framing to a subscriber.
 *
 * It is also hidden on every UNAUTHENTICATED page. Measured on a 390px viewport, the
 * floating stack sat on top of the password field of the sign-in form — and an assistant
 * that answers questions about your revenue has nothing to offer someone who has not
 * proved who they are yet. `public` covers the login and marketing routes;
 * `requiresAuth: false` covers the token-bearing ones like set-password, where a visitor
 * is mid-flow and equally has no use for it.
 */
const showAssistant = computed<boolean>(() =>
  // `route.name` is the "has the router actually resolved this yet" test, and it has to
  // come first. On the very first render route.meta is an EMPTY object, so all three
  // checks below pass, the drawer mounts, and it fires GET /api/ai/config with no token —
  // a 401 in the console of the sign-in page on every single load, before the route
  // resolves and unmounts it again. Every guard here was correct; none of them had
  // anything to read yet.
  route.name != null
  && route.meta['customerFacing'] !== true
  && route.meta['public'] !== true
  && route.meta['requiresAuth'] !== false,
)
</script>

<template>
  <div style="background:var(--canvas);min-height:100vh;color:var(--ink);">
    <RouterView />
    <ToastContainer />
    <!--
      Mounted once at the root, driven from anywhere by `useStepUp()`. One instance, because
      two dialogs racing to be the modal is a worse bug than the duplication it would avoid —
      and because a guarded action can be started from a page that then navigates away.
    -->
    <StepUpDialog />
    <AiChatDrawer v-if="showAssistant" />
  </div>
</template>
